import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, Copy, Trash2, ChevronUp, ChevronDown, Save, ArrowLeft, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Btn, Field, Area, Select } from "@/components/admin/ui";
import RichTextEditor from "@/components/editor/RichTextEditor";
import MediaPicker from "@/components/admin/MediaPicker";

const MODULE_TYPES = [
  ["richtext", "Rich Text"], ["image_text", "Image + Text"], ["service_cards", "Service Cards"],
  ["columns", "Columns"], ["gallery", "Image Gallery"], ["video", "Video"],
  ["testimonials", "Testimonials"], ["cta_banner", "Call-to-Action Banner"],
  ["contact_info", "Contact Info"], ["divider", "Divider / Spacing"],
];

const DEFAULTS = {
  richtext: { html: "<p>New text section…</p>" },
  image_text: { image: "", alt: "", heading: "Heading", html: "<p>Describe this section…</p>", layout: "left" },
  service_cards: { services: [{ title: "Service", summary: "Short description", image: "" }] },
  columns: { columns: 2, items: [{ html: "<p>Column one</p>" }, { html: "<p>Column two</p>" }] },
  gallery: { images: [] },
  video: { heading: "", url: "" },
  testimonials: { items: [{ author: "Client", quote: "Their words…" }] },
  cta_banner: { heading: "Ready to begin?", text: "", cta_label: "Book an Appointment", cta_url: "/contact" },
  contact_info: { phone: "+1 (289) 442-1265", email: "healthyroots4wellness@gmail.com", address: "Hamilton, Ontario, Canada" },
  divider: {},
};

const uid = () => Math.random().toString(36).slice(2, 9);

export default function PageEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [page, setPage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [picker, setPicker] = useState(null); // callback holder

  useEffect(() => { api.get(`/admin/pages/${id}`).then((r) => setPage(r.data)).catch(() => nav("/admin/pages")); }, [id, nav]);
  if (!page) return <div className="text-emerald-800">Loading…</div>;

  const set = (patch) => setPage({ ...page, ...patch });
  const setModule = (mid, data) => set({ modules: page.modules.map((m) => m.id === mid ? { ...m, data } : m) });
  const addModule = (type) => set({ modules: [...page.modules, { id: uid(), type, data: JSON.parse(JSON.stringify(DEFAULTS[type])) }] });
  const dupModule = (mid) => { const m = page.modules.find((x) => x.id === mid); set({ modules: [...page.modules, { ...JSON.parse(JSON.stringify(m)), id: uid() }] }); };
  const rmModule = (mid) => set({ modules: page.modules.filter((m) => m.id !== mid) });
  const move = (i, d) => { const a = [...page.modules]; const j = i + d; if (j < 0 || j >= a.length) return; [a[i], a[j]] = [a[j], a[i]]; set({ modules: a }); };

  const save = async (status) => {
    setSaving(true);
    try {
      const body = { ...page, status: status || page.status };
      const { data } = await api.put(`/admin/pages/${id}`, body);
      setPage(data);
      toast.success(status === "published" ? "Published" : status === "draft" ? "Unpublished (draft)" : "Saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const openPicker = (cb) => setPicker(() => cb);

  return (
    <div data-testid="page-editor">
      <PageHeader title="Edit Page" subtitle={`/${page.slug}`}>
        <Btn variant="outline" onClick={() => nav("/admin/pages")}><ArrowLeft size={16} /> Back</Btn>
        {page.status === "published"
          ? <Btn variant="outline" onClick={() => save("draft")}>Unpublish</Btn>
          : <Btn variant="outline" onClick={() => save("published")}>Publish</Btn>}
        <Btn onClick={() => save()} disabled={saving} data-testid="cms-save-page-btn"><Save size={16} /> {saving ? "Saving…" : "Save"}</Btn>
      </PageHeader>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-stone-200/80 space-y-4 h-fit">
          <h3 className="font-serif text-lg text-emerald-900">Page settings</h3>
          <Field label="Title" value={page.title} onChange={(v) => set({ title: v })} />
          <Field label="URL slug" value={page.slug} onChange={(v) => set({ slug: v })} />
          <Select label="Status" value={page.status} onChange={(v) => set({ status: v })} options={[{ value: "draft", label: "Draft" }, { value: "published", label: "Published" }]} />
          <label className="flex items-center gap-2 text-sm text-emerald-900"><input type="checkbox" checked={!!page.nav_visible} onChange={(e) => set({ nav_visible: e.target.checked })} className="accent-emerald-700" /> Show in navigation</label>
          <Field label="Nav order" type="number" value={page.nav_order} onChange={(v) => set({ nav_order: Number(v) })} />
          <hr className="border-stone-200" />
          <h3 className="font-serif text-lg text-emerald-900">SEO</h3>
          <Field label="SEO title" value={page.seo?.title} onChange={(v) => set({ seo: { ...page.seo, title: v } })} />
          <Area label="SEO description" value={page.seo?.description} onChange={(v) => set({ seo: { ...page.seo, description: v } })} />
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="flex flex-wrap gap-2 bg-white rounded-2xl p-4 border border-stone-200/80">
            <span className="text-sm text-stone-500 w-full mb-1">Add a section:</span>
            {MODULE_TYPES.map(([t, l]) => <button key={t} onClick={() => addModule(t)} data-testid={`add-module-${t}`} className="inline-flex items-center gap-1 text-xs rounded-lg border border-stone-300 px-3 py-1.5 hover:bg-emerald-50 text-emerald-800"><Plus size={13} /> {l}</button>)}
          </div>

          {page.modules.length === 0 && <div className="bg-white rounded-2xl p-10 border border-dashed border-stone-300 text-center text-stone-400">No sections yet. Add one above.</div>}

          {page.modules.map((m, i) => (
            <div key={m.id} className="bg-white rounded-2xl border border-stone-200/80 overflow-hidden" data-testid={`module-${m.type}`}>
              <div className="flex items-center justify-between px-4 py-2.5 bg-stone-50 border-b">
                <span className="text-sm font-semibold text-emerald-900">{MODULE_TYPES.find((x) => x[0] === m.type)?.[1] || m.type}</span>
                <div className="flex gap-1">
                  <button onClick={() => move(i, -1)} className="p-1.5 rounded hover:bg-stone-200 text-stone-500"><ChevronUp size={15} /></button>
                  <button onClick={() => move(i, 1)} className="p-1.5 rounded hover:bg-stone-200 text-stone-500"><ChevronDown size={15} /></button>
                  <button onClick={() => dupModule(m.id)} className="p-1.5 rounded hover:bg-stone-200 text-stone-500"><Copy size={15} /></button>
                  <button onClick={() => rmModule(m.id)} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 size={15} /></button>
                </div>
              </div>
              <div className="p-4"><ModuleForm m={m} setData={(d) => setModule(m.id, d)} openPicker={openPicker} /></div>
            </div>
          ))}
        </div>
      </div>

      <MediaPicker open={!!picker} onClose={() => setPicker(null)} onSelect={(url) => { picker && picker(url); }} />
    </div>
  );
}

function ImgField({ label, value, onChange, openPicker }) {
  return (
    <div>
      <span className="text-sm font-medium text-emerald-900">{label}</span>
      <div className="flex gap-2 mt-1">
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL" className="flex-1 rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-600" />
        <button onClick={() => openPicker((url) => onChange(url))} type="button" className="rounded-lg border border-stone-300 px-3 py-2 text-sm hover:bg-stone-100 flex items-center gap-1"><ImageIcon size={15} /> Library</button>
      </div>
      {value && <img src={value} alt="" className="mt-2 h-24 rounded-lg object-cover border" />}
    </div>
  );
}

function ModuleForm({ m, setData, openPicker }) {
  const d = m.data || {};
  const upd = (patch) => setData({ ...d, ...patch });
  switch (m.type) {
    case "richtext":
      return <RichTextEditor value={d.html} onChange={(html) => upd({ html })} />;
    case "image_text":
      return (
        <div className="space-y-3">
          <ImgField label="Image" value={d.image} onChange={(v) => upd({ image: v })} openPicker={openPicker} />
          <Field label="Image alt text" value={d.alt} onChange={(v) => upd({ alt: v })} />
          <Field label="Heading" value={d.heading} onChange={(v) => upd({ heading: v })} />
          <Select label="Image side" value={d.layout} onChange={(v) => upd({ layout: v })} options={[{ value: "left", label: "Left" }, { value: "right", label: "Right" }]} />
          <RichTextEditor value={d.html} onChange={(html) => upd({ html })} />
        </div>
      );
    case "service_cards":
      return <ListEditor items={d.services} onChange={(services) => upd({ services })} blank={{ title: "", summary: "", image: "" }}
        render={(it, set) => (<>
          <Field label="Title" value={it.title} onChange={(v) => set({ title: v })} />
          <Area label="Summary" value={it.summary} onChange={(v) => set({ summary: v })} rows={2} />
          <ImgField label="Image" value={it.image} onChange={(v) => set({ image: v })} openPicker={openPicker} />
        </>)} />;
    case "columns":
      return (
        <div className="space-y-3">
          <Select label="Number of columns" value={String(d.columns)} onChange={(v) => upd({ columns: Number(v) })} options={[1, 2, 3, 4].map((n) => ({ value: String(n), label: `${n}` }))} />
          <ListEditor items={d.items} onChange={(items) => upd({ items })} blank={{ html: "<p>Column</p>" }}
            render={(it, set) => <RichTextEditor value={it.html} onChange={(html) => set({ html })} />} />
        </div>
      );
    case "gallery":
      return <ListEditor items={d.images} onChange={(images) => upd({ images })} blank={{ url: "", alt: "" }}
        render={(it, set) => (<><ImgField label="Image" value={it.url} onChange={(v) => set({ url: v })} openPicker={openPicker} /><Field label="Alt text" value={it.alt} onChange={(v) => set({ alt: v })} /></>)} />;
    case "video":
      return (
        <div className="space-y-3">
          <Field label="Heading (optional)" value={d.heading} onChange={(v) => upd({ heading: v })} />
          <Field label="Video URL (YouTube, Vimeo, Rumble or .mp4)" value={d.url} onChange={(v) => upd({ url: v })} />
          <p className="text-xs text-stone-500">Only YouTube, Vimeo, Rumble and direct video URLs are embedded. Others are ignored for safety.</p>
        </div>
      );
    case "testimonials":
      return <ListEditor items={d.items} onChange={(items) => upd({ items })} blank={{ author: "", quote: "" }}
        render={(it, set) => (<><Field label="Author" value={it.author} onChange={(v) => set({ author: v })} /><Area label="Quote" value={it.quote} onChange={(v) => set({ quote: v })} /></>)} />;
    case "cta_banner":
      return (<div className="space-y-3">
        <Field label="Heading" value={d.heading} onChange={(v) => upd({ heading: v })} />
        <Area label="Text" value={d.text} onChange={(v) => upd({ text: v })} rows={2} />
        <Field label="Button label" value={d.cta_label} onChange={(v) => upd({ cta_label: v })} />
        <Field label="Button link" value={d.cta_url} onChange={(v) => upd({ cta_url: v })} />
      </div>);
    case "contact_info":
      return (<div className="space-y-3">
        <Field label="Phone" value={d.phone} onChange={(v) => upd({ phone: v })} />
        <Field label="Email" value={d.email} onChange={(v) => upd({ email: v })} />
        <Field label="Address" value={d.address} onChange={(v) => upd({ address: v })} />
      </div>);
    case "divider":
      return <p className="text-sm text-stone-400">A horizontal divider will render here.</p>;
    default:
      return null;
  }
}

function ListEditor({ items = [], onChange, blank, render }) {
  const set = (i, patch) => onChange(items.map((it, j) => j === i ? { ...it, ...patch } : it));
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="rounded-xl border border-stone-200 p-3 space-y-2 relative">
          <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="absolute top-2 right-2 text-red-500 hover:bg-red-50 rounded p-1"><Trash2 size={14} /></button>
          {render(it, (patch) => set(i, patch))}
        </div>
      ))}
      <button onClick={() => onChange([...items, JSON.parse(JSON.stringify(blank))])} className="text-sm text-emerald-700 inline-flex items-center gap-1 font-semibold"><Plus size={14} /> Add item</button>
    </div>
  );
}
